// Admin-only "Delete …" affordance — the shared UI for soft-deleting a project
// or an account (CEO walkthrough note 2026-07-29: "How do I delete a project, or
// an account? I don't see an option."). One component, two call sites
// (ProjectDetailPage header, ClientDetailPage header), so the admin gate, the
// confirm dialog, the honest-error surfacing, and the navigate-on-success flow
// can't drift between them.
//
// The four guardrails all live in @merchd/shared (pure, DB-free, and executed by
// the offline gate qa-delete-actions):
//   - canSeeDeleteAction     — non-admins never render the action at all
//   - accountDeleteBlockReason (passed in as `blockReason`) — pre-disable with an
//     honest reason (round-47 honest gate) rather than click-then-409
//   - mayFireDelete          — the destructive call fires ONLY from the confirmed
//     dialog state; the trigger just opens the dialog
//   - extractDeleteErrorMessage — a server refusal reaches the user VERBATIM,
//     never a generic failure line
//   - buildDeleteSuccessNav  — go back to the list with a confirmation flash
//
// The server (requireRole("admin") + softDeleteProject / softDeleteClient) is
// the authoritative backstop; this is the affordance + honest client mirror.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Trash2, X } from "lucide-react";
import {
  buildDeleteSuccessNav,
  canSeeDeleteAction,
  deleteRefusalCode,
  extractDeleteErrorMessage,
  mayFireDelete,
  type DeletePhase,
} from "@merchd/shared";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/auth/AuthProvider";

export interface DeleteEntityActionProps {
  /** The DELETE endpoint path, e.g. `/projects/${id}` or `/clients/${id}`. */
  deletePath: string;
  /** Trigger + confirm button label, e.g. "Delete project". */
  triggerLabel: string;
  /** Confirmation dialog heading, e.g. "Delete this project?". */
  confirmTitle: string;
  /** Description of what happens — HONEST about the soft delete (leaves the
   *  workspace, no in-app undo). ReactNode so callers can bold the entity name
   *  and interpolate live document context. */
  confirmBody: ReactNode;
  /** Pre-disable reason (honest gate) — null/undefined when deletable. */
  blockReason?: string | null;
  /** Where to navigate on success (the entity's list route). */
  redirectTo: string;
  /** Confirmation flash shown on the list page after the delete. */
  flashMessage: string;
  /** Entity-specific fallback error (never a generic failure line) when the
   *  server sends no usable message. */
  errorFallback: string;
  /** Who may delete — default admins only. Things a rep enters (contacts,
   *  addresses) pass the roles that can edit them (Stephanie 2026-10-30:
   *  "anything that a rep enters should be editable and allow us to delete"). */
  allowedRoles?: readonly string[];
  /** React Query keys to invalidate on success (list caches). */
  invalidateKeys?: readonly (readonly unknown[])[];
  /** Honest-gate pre-warning (round-47 pattern) shown in the dialog on FIRST
   *  open — BEFORE any submit — so the admin discovers a known consequence
   *  up front instead of by a refused delete. The caller derives it (e.g. runs
   *  projectSentDocsRefusal on already-fetched counts); null when there's nothing
   *  to warn about. The server DELETE stays authoritative — this is discovery,
   *  not enforcement. Hidden once a live server error replaces it. */
  preWarning?: ReactNode;
  /** Audited admin override (round-53 sent-documents guard). OPT-IN: only call
   *  sites that face a structured, overridable server refusal pass this (the
   *  project delete does; the account delete does not — it's blocked upstream
   *  while projects exist). When present and the DELETE is refused with a 409
   *  whose `error` code === `override.errorCode`, the dialog offers an admin
   *  "Delete anyway" button that re-fires the DELETE with `{ [override.flag]:
   *  true }` in the body, plus the honest consequence copy. */
  override?: {
    /** Request-body flag the server reads to bypass the guard (audited). */
    flag: string;
    /** The 409 `error` code that unlocks the override affordance. */
    errorCode: string;
    /** The override button label, e.g. "Delete anyway". */
    actionLabel: string;
    /** Honest consequence copy shown beside the override button. */
    consequenceNote: ReactNode;
  };
}

export function DeleteEntityAction(props: DeleteEntityActionProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<DeletePhase>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Whether the last refusal was the specific, overridable one this call site
  // knows how to override (sent documents). Gates the admin "Delete anyway"
  // affordance — never shown pre-emptively, only after the server refuses.
  const [overrideAvailable, setOverrideAvailable] = useState(false);
  // The override INTENT rides a ref (not state) so the single destructive
  // mutate call reads it synchronously — no second call site to bypass the
  // mayFireDelete guard, no stale-state race.
  const overrideRef = useRef(false);

  const mutation = useMutation({
    mutationFn: () =>
      api(props.deletePath, {
        method: "DELETE",
        ...(overrideRef.current && props.override
          ? { body: JSON.stringify({ [props.override.flag]: true }) }
          : {}),
      }),
    onMutate: () => setPhase("deleting"),
    onError: (err) => {
      // Honest surfacing — the server's own words reach the user, in the dialog,
      // which stays open so they can read the reason (and retry if it was transient).
      setErrorMsg(extractDeleteErrorMessage(err, props.errorFallback));
      // Offer the admin override ONLY when the server refused with the exact
      // structured code this call site can override (sent documents). Set from the
      // CURRENT error's code every time, so a LATER failure with a different code
      // (e.g. a 404) clears a stale "Delete anyway" affordance.
      setOverrideAvailable(
        !!props.override &&
          deleteRefusalCode(err) === props.override.errorCode,
      );
      setPhase("confirming");
    },
    onSuccess: () => {
      for (const key of props.invalidateKeys ?? []) {
        void queryClient.invalidateQueries({ queryKey: key as unknown[] });
      }
      const nav = buildDeleteSuccessNav(props.redirectTo, props.flashMessage);
      navigate(nav.to, { state: nav.state });
    },
  });

  // Close the confirmation dialog on Escape (guarded while the delete is in
  // flight) — the same keydown pattern OrderAddressModal / SendToCustomerModal /
  // EnterBillModal / CloneTargetModal use. Hook runs unconditionally (Rules of
  // Hooks); no-ops when the dialog is closed.
  useEffect(() => {
    if (phase === "idle") return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !mutation.isPending) {
        setPhase("idle");
        setErrorMsg(null);
        setOverrideAvailable(false);
        overrideRef.current = false;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, mutation.isPending]);

  // Non-admins never see the action. Hooks above run unconditionally (Rules of
  // Hooks); the gate is the render.
  if (props.allowedRoles ? !user?.roles?.some((r) => props.allowedRoles!.includes(r)) : !canSeeDeleteAction(user?.roles)) return null;

  const blockReason = props.blockReason ?? null;
  const blocked = blockReason !== null;

  const openDialog = () => {
    setErrorMsg(null);
    setOverrideAvailable(false);
    overrideRef.current = false;
    setPhase("confirming");
  };
  const closeDialog = () => {
    if (mutation.isPending) return;
    setPhase("idle");
    setErrorMsg(null);
    setOverrideAvailable(false);
    overrideRef.current = false;
  };
  const confirmDelete = () => {
    // The destructive call may fire ONLY from the confirmed state. Both the
    // primary confirm and the admin override route through here (setting
    // overrideRef first), so there's a single mutate() call site behind the guard.
    if (!mayFireDelete(phase)) return;
    mutation.mutate();
  };
  const onConfirmClick = () => {
    overrideRef.current = false;
    confirmDelete();
  };
  const onOverrideClick = () => {
    // Admin-only, and only reachable after a structured refusal (overrideAvailable).
    overrideRef.current = true;
    confirmDelete();
  };
  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Click the dimmed backdrop (not the panel) to dismiss — guarded in-flight.
    if (e.target === e.currentTarget && !mutation.isPending) {
      setPhase("idle");
      setErrorMsg(null);
      setOverrideAvailable(false);
      overrideRef.current = false;
    }
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={openDialog}
        disabled={blocked}
        className="gap-1.5 text-brown-400 hover:bg-rust-50 hover:text-rust-600"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
        {props.triggerLabel}
      </Button>

      {/* Honest gate (round-47): when the action is pre-disabled, the reason is
          VISIBLE inline — never hover-only — so a rep knows why without probing
          the disabled button. Mirrors NextActionCta's disabled-reason line. */}
      {blocked && (
        <p className="max-w-xs text-sm text-brown-400">{blockReason}</p>
      )}

      {phase !== "idle" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={props.confirmTitle}
          onClick={onBackdropClick}
        >
          <div className="flex w-full max-w-md flex-col rounded-xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-brown-50 px-6 py-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 rounded-full bg-rust-50 p-1.5 text-rust">
                  <AlertTriangle className="h-5 w-5" aria-hidden />
                </span>
                <h2 className="font-display text-xl font-semibold text-brown-500">
                  {props.confirmTitle}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeDialog}
                disabled={mutation.isPending}
                className="rounded-md p-1 text-brown-300 hover:bg-cream-50 hover:text-brown-500 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <p className="text-sm leading-relaxed text-brown-500">
                {props.confirmBody}
              </p>
              {/* Honest pre-warning (round-47 gate) — surfaced on first open, and
                  only until a live server error replaces it, so the admin isn't
                  reading a stale client guess after the authoritative refusal. */}
              {props.preWarning && !errorMsg && (
                <p className="rounded-md border border-rust-200 bg-cream-50 px-3 py-2 text-sm text-brown-500">
                  {props.preWarning}
                </p>
              )}
              {errorMsg && (
                <p className="rounded-md border border-rust-200 bg-rust-50 px-3 py-2 text-sm text-brown-500">
                  {errorMsg}
                </p>
              )}
              {/* Admin override consequence copy — shown only after a structured
                  sent-documents refusal, beside the "Delete anyway" button.
                  Honest: it does NOT pretend the public links get revoked. */}
              {overrideAvailable && props.override && (
                <p className="rounded-md border border-rust-200 bg-cream-50 px-3 py-2 text-sm text-brown-500">
                  {props.override.consequenceNote}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-brown-50 px-6 py-4">
              <Button
                type="button"
                variant="ghost"
                onClick={closeDialog}
                disabled={mutation.isPending}
                autoFocus
              >
                Cancel
              </Button>
              {overrideAvailable && props.override ? (
                // Post-refusal: the primary confirm would just re-trigger the same
                // 409, so replace it with the audited admin override.
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onOverrideClick}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? "Deleting…" : props.override.actionLabel}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onConfirmClick}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? "Deleting…" : props.triggerLabel}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
